<template>
    <div>
        <template v-if="!service">
            <FormControl
                required
                label="Type"
                :error="
                    errors?.getValidationError(
                        RequestBodyKeyEnum.App_UpdateService_SchedulingStrategy,
                    )
                "
            >
                <div class="space-y-1">
                    <Selectable
                        v-slot="selectable"
                        :value="SchedulingStrategyEnum.SingleHostSingleAttendee"
                        v-model="dummySelectedSchedulingStrategy"
                    >
                        <div
                            class="p-3 rounded-lg2 bg-white border-2 border-gray-200 shadow-xs hover:border-primary"
                            :class="{ 'border-primary': selectable.active }"
                        >
                            <div
                                class="flex justify-start items-center gap-2 relative"
                            >
                                <div
                                    v-if="selectable.active"
                                    class="absolute right-0 top-0"
                                >
                                    <Icon
                                        name="circle-check"
                                        class="w-6 h-6 text-primary"
                                    />
                                </div>
                                <div>
                                    <div>
                                        {{
                                            Service.getSchedulingStrategyLabel(
                                                SchedulingStrategyEnum.SingleHostSingleAttendee,
                                            )
                                        }}
                                    </div>
                                    <div class="text-xs text-gray-500">
                                        {{
                                            Service.getSchedulingStrategyDescription(
                                                SchedulingStrategyEnum.SingleHostSingleAttendee,
                                            )
                                        }}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Selectable>
                    <Selectable
                        v-slot="selectable"
                        :value="
                            SchedulingStrategyEnum.SingleHostMultipleAttendee
                        "
                        v-model="dummySelectedSchedulingStrategy"
                    >
                        <div
                            class="p-3 rounded-lg2 bg-white border-2 border-gray-200 shadow-xs hover:border-primary"
                            :class="{ 'border-primary': selectable.active }"
                        >
                            <div
                                class="flex justify-start items-center gap-2 relative"
                            >
                                <div
                                    v-if="selectable.active"
                                    class="absolute right-0 top-0"
                                >
                                    <Icon
                                        name="circle-check"
                                        class="w-6 h-6 text-primary"
                                    />
                                </div>
                                <div>
                                    <div>
                                        {{
                                            Service.getSchedulingStrategyLabel(
                                                SchedulingStrategyEnum.SingleHostMultipleAttendee,
                                            )
                                        }}
                                    </div>
                                    <div class="text-xs text-gray-500">
                                        {{
                                            Service.getSchedulingStrategyDescription(
                                                SchedulingStrategyEnum.SingleHostMultipleAttendee,
                                            )
                                        }}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Selectable>
                    <input
                        type="hidden"
                        :name="
                            RequestBodyKeyEnum.App_UpdateService_SchedulingStrategy
                        "
                        :value="dummySelectedSchedulingStrategy"
                    />
                </div>
            </FormControl>
        </template>

        <template
            v-if="
                !service &&
                Service.isMultipleAttendeesPerMeetingSupported(
                    dummySelectedSchedulingStrategy,
                )
            "
        >
            <FormControl
                required
                :label="`Maximum number of seats (min: 2, max: ${MAXIMUM_NUMBER_OF_SEATS_PER_MEETING})`"
                :error="
                    errors?.getValidationError(
                        RequestBodyKeyEnum.App_UpdateService_MaximumNumberOfSeatsPerMeeting,
                    )
                "
            >
                <TextInput
                    type="number"
                    inputmode="numeric"
                    step="1"
                    min="2"
                    max="50"
                    :name="
                        RequestBodyKeyEnum.App_UpdateService_MaximumNumberOfSeatsPerMeeting
                    "
                    :value="2"
                    required
                />
            </FormControl>
        </template>
        <template v-else>
            <input
                type="hidden"
                :name="
                    RequestBodyKeyEnum.App_UpdateService_MaximumNumberOfSeatsPerMeeting
                "
                value="1"
            />
        </template>
    </div>

    <FormControl
        required
        label="Title"
        :error="
            errors?.getValidationError(
                RequestBodyKeyEnum.App_UpdateService_Title,
            )
        "
    >
        <TextInput
            :name="RequestBodyKeyEnum.App_UpdateService_Title"
            :value="service?.title"
            required
        />
    </FormControl>

    <FormControl
        label="Description"
        :error="
            errors?.getValidationError(
                RequestBodyKeyEnum.App_UpdateService_Description,
            )
        "
    >
        <TextareaInput
            :name="RequestBodyKeyEnum.App_UpdateService_Description"
            rows="3"
            :value="service?.description"
        />
    </FormControl>

    <template v-if="service">
        <FormControl
            required
            label="Slug"
            :error="
                errors?.getValidationError(
                    RequestBodyKeyEnum.App_UpdateService_Slug,
                )
            "
        >
            <TextInput
                :name="RequestBodyKeyEnum.App_UpdateService_Slug"
                :value="service.slug"
                required
            />
        </FormControl>
    </template>

    <FormControl
        label="Duration (in minutes)"
        :error="
            errors?.getValidationError(
                RequestBodyKeyEnum.App_UpdateService_DurationInMinutes,
            )
        "
    >
        <TextInput
            type="number"
            inputmode="numeric"
            step="1"
            :name="RequestBodyKeyEnum.App_UpdateService_DurationInMinutes"
            :value="service?.durationInMinutes ?? '60'"
        />
    </FormControl>

    <FormControl
        label="Meeting locations"
        :error="
            errors?.getValidationError(
                RequestBodyKeyEnum.App_UpdateService_EnabledMeetingLocationIds,
            )
        "
        required
    >
        <Table>
            <TableHead>
                <TableHeadCell>Type</TableHeadCell>
            </TableHead>
            <TableBody>
                <template
                    v-for="meetingLocation in meetingLocations"
                    :key="meetingLocation.id"
                >
                    <TableBodyRow
                        v-if="
                            Service.isMeetingLocationAllowed(
                                dummySelectedSchedulingStrategy,
                                meetingLocation.type,
                            )
                        "
                    >
                        <TableBodyRowCell>
                            <CheckboxInput
                                :label="meetingLocation.label"
                                :value="meetingLocation.id"
                                :checked="
                                    serviceAggregate?.isMeetingLocationEnabled(
                                        meetingLocation.id,
                                    ) ?? false
                                "
                                :name="`${RequestBodyKeyEnum.App_UpdateService_EnabledMeetingLocationIds}[]`"
                            />
                        </TableBodyRowCell>
                    </TableBodyRow>
                </template>
            </TableBody>
        </Table>

        <div
            v-if="service"
            class="flex justify-end mt-2"
        >
            <Button
                as="a"
                :href="
                    urlFactory.makeCompanyEditUrl(
                        `#${IN_PERSON_MEETING_LOCATIONS_SECTION_ID}`,
                    )
                "
                iconName="arrow-right"
                iconClass="w-4 h-4"
                color="light"
                :iconRight="true"
            >
                Manage In Person Locations
            </Button>
        </div>
    </FormControl>

    <FormControl
        label="Visibility"
        :error="
            errors?.getValidationError(
                RequestBodyKeyEnum.App_UpdateService_IsVisible,
            )
        "
    >
        <CheckboxInput
            label="Visible"
            :name="RequestBodyKeyEnum.App_UpdateService_IsVisible"
            :checked="service ? service.isVisible : true"
        />
    </FormControl>

    <FormControl
        label="Enable booking"
        :error="
            errors?.getValidationError(
                RequestBodyKeyEnum.App_UpdateService_IsEnabled,
            )
        "
    >
        <CheckboxInput
            label="Enabled"
            :name="RequestBodyKeyEnum.App_UpdateService_IsEnabled"
            :checked="service ? service.isEnabled : true"
        />
    </FormControl>
</template>

<script setup lang="ts">
    import { UrlFactory } from '@/Shared/Url/UrlFactory'
    import ServiceAggregate from '@/App/Service/Entity/ServiceAggregate'
    import { ref, type PropType } from 'vue'
    import FormControl from '@/Shared/Component/Forms/FormControl.vue'
    import TextInput from '@/Shared/Component/Forms/TextInput.vue'
    import CheckboxInput from '@/Shared/Component/Forms/CheckboxInput.vue'
    import Table from '@/Shared/Component/Table/Table.vue'
    import TableBody from '@/Shared/Component/Table/TableBody.vue'
    import TableBodyRow from '@/Shared/Component/Table/TableBodyRow.vue'
    import TableBodyRowCell from '@/Shared/Component/Table/TableBodyRowCell.vue'
    import TableHead from '@/Shared/Component/Table/TableHead.vue'
    import TableHeadCell from '@/Shared/Component/Table/TableHeadCell.vue'
    import TextareaInput from '@/Shared/Component/Forms/TextareaInput.vue'
    import ApiFormErrors from '@/Shared/Component/Forms/ApiFormErrors'
    import {
        MAXIMUM_NUMBER_OF_SEATS_PER_MEETING,
        SchedulingStrategyEnum,
        type MeetingLocationType,
    } from '@/Shared/AutoGenerated/Main'
    import Selectable from '@/Shared/Component/Selectable.vue'
    import Icon from '@/Shared/Component/Icon.vue'
    import Service from '@/App/Service/Entity/Service'
    import Button from '@/Shared/Component/Button.vue'
    import Company from '@/App/Company/Entity/Company'
    import { IN_PERSON_MEETING_LOCATIONS_SECTION_ID } from '@/Shared/AutoGenerated/Types'
    import { RequestBodyKeyEnum } from '@/Shared/AutoGenerated/RequestFieldEnum'

    const props = defineProps({
        serviceAggregate: {
            type: Object as PropType<ServiceAggregate | null>,
            default: null,
        },
        meetingLocations: {
            type: Array as PropType<MeetingLocationType[]>,
            required: true,
        },
        errors: {
            type: Object as PropType<ApiFormErrors>,
            required: true,
        },
    })

    const service = props.serviceAggregate?.service
    const dummySelectedSchedulingStrategy = ref<SchedulingStrategyEnum>(
        service?.schedulingStrategy ??
            SchedulingStrategyEnum.SingleHostSingleAttendee,
    )
    const urlFactory = new UrlFactory()
</script>
